// Shared shape hit-detection — mirrors backend hitbox_service. All coords
// are normalized 0..100. Padding is applied uniformly per shape.

/**
 * Axis-aligned bounding box of a polygon (0..100 normalized coords).
 * Returns null when the polygon has fewer than 3 vertices.
 * The LevelEditor calls this after every polygon-vertex mutation so the
 * paired `xPercent/yPercent/widthPercent/heightPercent` bbox fields stay in
 * sync with `hitboxPolygon` — otherwise child gameplay's rect-based UI
 * (found checkmark, keyboard-focus outline, tap-catcher <div>) sits at the
 * pre-edit location while the polygon has moved.
 */
export function polygonBbox(poly) {
  if (!poly || poly.length < 3) return null;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const [x, y] of poly) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return {
    xPercent: minX,
    yPercent: minY,
    widthPercent: maxX - minX,
    heightPercent: maxY - minY,
  };
}

/**
 * Axis-aligned bounding box of a circle (0..100 normalized coords). Returns
 * null when the circle spec is missing. Used by LevelEditor when the circle
 * center/radius changes to keep the paired bbox fields consistent.
 */
export function circleBbox(circle) {
  if (!circle || typeof circle.cx !== "number" || typeof circle.radius !== "number") return null;
  return {
    xPercent: Math.max(0, circle.cx - circle.radius),
    yPercent: Math.max(0, circle.cy - circle.radius),
    widthPercent: Math.min(100, circle.radius * 2),
    heightPercent: Math.min(100, circle.radius * 2),
  };
}

export function paddedRect(o) {
  const pad = o.hitboxPaddingPercent ?? 0;
  const padX = (o.widthPercent * pad) / 100;
  const padY = (o.heightPercent * pad) / 100;
  return {
    left: Math.max(0, o.xPercent - padX),
    top: Math.max(0, o.yPercent - padY),
    right: Math.min(100, o.xPercent + o.widthPercent + padX),
    bottom: Math.min(100, o.yPercent + o.heightPercent + padY),
  };
}

export function pointInRect(x, y, o) {
  const r = paddedRect(o);
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

export function pointInCircle(x, y, o) {
  const c = o.hitboxCircle;
  if (!c) return false;
  const pad = o.hitboxPaddingPercent ?? 0;
  const r = c.radius * (1 + pad / 100);
  const dx = x - c.cx;
  const dy = y - c.cy;
  return dx * dx + dy * dy <= r * r;
}

export function pointInPolygon(x, y, o) {
  const poly = o.hitboxPolygon;
  if (!poly || poly.length < 3) return false;
  const pad = o.hitboxPaddingPercent ?? 0;
  const cx = poly.reduce((s, p) => s + p[0], 0) / poly.length;
  const cy = poly.reduce((s, p) => s + p[1], 0) / poly.length;
  const scale = 1 + pad / 100;
  const padded = poly.map(([px, py]) => [cx + (px - cx) * scale, cy + (py - cy) * scale]);

  let inside = false;
  const n = padded.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = padded[i];
    const [xj, yj] = padded[j];
    if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

export function isPointInHitbox(x, y, o) {
  const shape = o.hitboxShape || "rectangle";
  if (shape === "circle" && o.hitboxCircle) return pointInCircle(x, y, o);
  if (shape === "polygon" && o.hitboxPolygon?.length >= 3) return pointInPolygon(x, y, o);
  return pointInRect(x, y, o);
}

// ─── Debug-overlay helpers ─────────────────────────────────────────────
// Expose the EFFECTIVE PADDED shape so the debug UI can render the exact
// zone the tap-check credits. With padding = 0 these equal the raw shape.

export function effectivePaddedRect(o) {
  return paddedRect(o);
}

export function effectivePaddedPolygon(o) {
  const poly = o.hitboxPolygon;
  if (o.hitboxShape !== "polygon" || !poly || poly.length < 3) return null;
  const pad = o.hitboxPaddingPercent ?? 0;
  const cx = poly.reduce((s, p) => s + p[0], 0) / poly.length;
  const cy = poly.reduce((s, p) => s + p[1], 0) / poly.length;
  const scale = 1 + pad / 100;
  return poly.map(([px, py]) => [cx + (px - cx) * scale, cy + (py - cy) * scale]);
}

export function effectivePaddedCircle(o) {
  const c = o.hitboxCircle;
  if (o.hitboxShape !== "circle" || !c) return null;
  const pad = o.hitboxPaddingPercent ?? 0;
  return { cx: c.cx, cy: c.cy, radius: c.radius * (1 + pad / 100) };
}
